import type { ReactNode } from 'react';
import { Info } from 'lucide-react';

import { Tooltip } from '@/components/Tooltip/Tooltip';

import styles from './ProfileField.module.css';

interface ProfileFieldProps {
  autoComplete?: string;
  error?: string;
  id: string;
  inputMode?: 'email' | 'text';
  isEditing: boolean;
  label: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  tooltipContent?: ReactNode;
  type?: 'email' | 'text';
  value: string;
}

export function ProfileField({
  autoComplete,
  error,
  id,
  inputMode = 'text',
  isEditing,
  label,
  onChange,
  readOnly = false,
  tooltipContent,
  type = 'text',
  value,
}: ProfileFieldProps) {
  const errorId = `${id}-error`;
  const displayValue = value.trim().length > 0 ? value : 'Not provided';

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {tooltipContent ? (
          <Tooltip content={tooltipContent}>
            <button
              aria-label={`About ${label}`}
              className={styles.infoButton}
              type="button"
            >
              <Info aria-hidden="true" size={15} />
            </button>
          </Tooltip>
        ) : null}
      </div>

      {isEditing && !readOnly ? (
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? 'true' : undefined}
          autoComplete={autoComplete}
          className={styles.input}
          id={id}
          inputMode={inputMode}
          onChange={(event) => onChange?.(event.target.value)}
          type={type}
          value={value}
        />
      ) : (
        <p
          className={styles.value}
          data-empty={value.trim().length === 0 ? 'true' : 'false'}
          id={id}
        >
          {displayValue}
        </p>
      )}

      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
